import React from "react";
import { HiOutlineExclamationCircle } from "react-icons/hi";

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg p-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-full">
                <HiOutlineExclamationCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Something went wrong
                </h1>
                <p className="text-gray-500 dark:text-gray-400">
                  An unexpected error has occurred
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-slate-900 rounded-md">
                <p className="font-medium text-gray-900 dark:text-white mb-2">
                  Error Message:
                </p>
                <p className="text-gray-600 dark:text-gray-300">
                  {this.state.error.message}
                </p>
              </div>

              {process.env.NODE_ENV === "development" && (
                <div className="p-4 bg-gray-50 dark:bg-slate-900 rounded-md">
                  <p className="font-medium text-gray-900 dark:text-white mb-2">
                    Stack Trace:
                  </p>
                  <pre className="text-sm text-gray-600 dark:text-gray-300 overflow-x-auto">
                    {this.state.error.stack}
                  </pre>
                </div>
              )}

              <button
                onClick={() => window.location.reload()}
                className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
